import React, { useState } from "react";
import styled from "styled-components";

import IconButton from "../../../../components/button/IconButton";
import ToolTip from "../../../../components/tooltip/ToolTip";
import { TASK_SORT_ARROW_ICON, type TaskSortDirection } from "./taskBoardUtils";

/** 並び替えボタンの説明（次にクリックしたときの挙動まで含めて示す） */
const SORT_BUTTON_TITLE: Record<TaskSortDirection | "none", string> = {
  none: "この列で並び替え（日付 / 内容の昇順）",
  asc: "昇順で並び替え中 — クリックで降順に",
  desc: "降順で並び替え中 — クリックで既定（期日順）に戻す",
};

const HeaderRoot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  overflow: visible;

  .task-col-actions {
    /* 削除アイコンは「ラベル＋トグル」と距離を取りたいので、セル右端に押し出す */
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 0px;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &:hover .task-col-actions,
  &:hover .task-col-sort {
    opacity: 1;
  }
`;

/**
 * 並び替えボタンの置き場。並び替えが効いていない列ではホバーするまで隠し、
 * 列ラベルの視認性を落とさないようにする（適用中は常時表示）。
 */
const SortSlot = styled.span.attrs({ className: "task-col-sort" })<{
  $active: boolean;
}>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.15s;
`;

/**
 * タイトルテキスト。コンテンツサイズで縮み、はみ出しは ellipsis。
 * `flex: 0 1 auto` にすることで、トグル（BoundaryToggle）がテキストの直後に
 * 密着して配置できる（残りスペースはトグルと削除アイコンの間に空く）。
 */
const TitleText = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: text;
`;

const TitleInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0 4px;
  font-size: 13px;
  line-height: 150%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--soremo-light-gray);
`;

/**
 * 「自列と右隣列の境界線」の状態（並列／直列）を切り替えるトグル。
 * - 並列（`$parallel=true`）: `chevron_right` を薄く表示し、境界は引かない
 * - 直列（`$parallel=false`）: `lock` を表示し、CSS 側で太い縦境界線が引かれる
 *   （実際の境界線描画は Table.tsx の `task-col-serial` ルール）
 * 最後の列（右隣が無い列）では呼び出し側で非表示にする。
 */
const BoundaryToggle = styled.button.attrs({ type: "button" })<{
  $parallel: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  /* タイトルテキストの直後に密着して配置する（セル右端ではない） */
  border: none;
  background: transparent;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  padding: 0;

  & .material-symbols-rounded {
    font-size: 16px;
    color: var(--soremo-light-gray);
  }
`;

interface TaskColumnHeaderProps {
  title: string;
  editable: boolean;
  /**
   * 「自列と右隣列の境界」が並列（線無し）か。`true` で並列、`false` で直列。
   * トグル UI は **左セル**（=自列）に置き、右隣のラインの有無を切り替える設計。
   */
  nextParallel: boolean;
  /** 右隣の列が存在するか（末尾列ではトグル非表示にする） */
  hasNextColumn: boolean;
  // eslint-disable-next-line no-unused-vars
  onRename: (title: string) => void;
  /** 「自列と右隣列の境界」を直列／並列でトグルする */
  onToggleNextBoundary: () => void;
  onDelete: () => void;
  /** この列で並び替え中の向き。null = この列では並び替えていない */
  sortDirection?: TaskSortDirection | null;
  /** 並び替えの切り替え（昇順 → 降順 → 既定）。未指定で並び替え UI 非表示 */
  onToggleSort?: () => void;
}

const TaskColumnHeader: React.FC<TaskColumnHeaderProps> = ({
  title,
  editable,
  nextParallel,
  hasNextColumn,
  onRename,
  onToggleNextBoundary,
  onDelete,
  sortDirection = null,
  onToggleSort,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  const commit = () => {
    setIsEditing(false);
    const trimmed = draft.trim();
    if (!trimmed || trimmed === title) {
      setDraft(title);
      return;
    }
    onRename(trimmed);
  };

  const boundaryDescription = nextParallel
    ? "並列（右隣は左の完了を待たない）— クリックで直列に"
    : "直列（右隣は左の完了を待つ）— クリックで並列に";
  const boundaryToggle = hasNextColumn ? (
    <ToolTip description={boundaryDescription}>
      <BoundaryToggle
        $parallel={nextParallel}
        aria-label="右側の境界線（直列／並列）を切り替え"
        // ヘッダー D&D とトグルクリックの競合を防ぐため、pointer 段階で
        // 親（ヘッダー）のドラッグセンサーへ伝播させない。
        onPointerDown={editable ? (e) => e.stopPropagation() : undefined}
        onClick={
          editable
            ? (e) => {
                e.stopPropagation();
                onToggleNextBoundary();
              }
            : undefined
        }
      >
        <span className="material-symbols-rounded">
          {nextParallel ? "chevron_right" : "lock"}
        </span>
      </BoundaryToggle>
    </ToolTip>
  ) : null;

  const sortButton = onToggleSort ? (
    // ヘッダー D&D にクリックを取られないよう pointer 段階で伝播を止める
    <SortSlot
      $active={!!sortDirection}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <IconButton
        icon={sortDirection ? TASK_SORT_ARROW_ICON[sortDirection] : "swap_vert"}
        size={14}
        color={sortDirection ? "var(--soremo-blue)" : undefined}
        title={SORT_BUTTON_TITLE[sortDirection ?? "none"]}
        ariaLabel="この列で並び替え"
        onClick={(e) => {
          e.stopPropagation();
          onToggleSort();
        }}
      />
    </SortSlot>
  ) : null;

  if (!editable) {
    return (
      <HeaderRoot>
        <ToolTip description={title} truncatedOnly>
          <TitleText>{title}</TitleText>
        </ToolTip>
        {boundaryToggle}
        {sortButton}
      </HeaderRoot>
    );
  }

  return (
    <HeaderRoot>
      {isEditing ? (
        <TitleInput
          autoFocus
          value={draft}
          // 編集中は input 操作と D&D が競合しないようにドラッグ起動を抑止する
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              setDraft(title);
              setIsEditing(false);
            }
          }}
        />
      ) : (
        <ToolTip description={title} truncatedOnly>
          <TitleText
            onClick={() => {
              setDraft(title);
              setIsEditing(true);
            }}
          >
            {title}
          </TitleText>
        </ToolTip>
      )}
      {!isEditing && boundaryToggle}
      {!isEditing && sortButton}
      {!isEditing && (
        // 削除ボタンの操作も D&D に取られないように pointer 段階でブロックする
        <span
          className="task-col-actions"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <IconButton
            icon="delete"
            size={14}
            title="列を削除"
            ariaLabel="列を削除"
            onClick={onDelete}
          />
        </span>
      )}
    </HeaderRoot>
  );
};

export default TaskColumnHeader;
